import * as React from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InsightsIcon from '@mui/icons-material/Insights';
import AddIcon from '@mui/icons-material/Add';
import Tooltip from '@mui/material/Tooltip';
//import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 30,
            top: 30,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function SaveInsightsModal({message} :any) {
  const [open, setOpen] = React.useState(false);
  const [projects, setProjects] = React.useState([]);
  const [selectedProject, setSelectedProject] = React.useState('');
  const [showNewProject, setShowNewProject] = React.useState(false);
  const [newProjectName, setNewProjectName] = React.useState('');
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  React.useEffect(() => {
    fetch('/projects')
      .then(function(response) {
        return response.json();
      })
      .then(function(data) {
        setProjects(data.filter((project: any) => !project.autonomous));
      })
      .catch(error => {console.log(error?.message); console.log('something went wrong');})
  }, [])


  const handleClickOpen = (e) => {
    e.preventDefault();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setShowNewProject(false);
    setNewProjectName('');
  };

  const handleSave = () => {
    if (!selectedProject) {
      // Show an error message or handle the case when no project is selected
      console.error('Please select a project');
      return;
    }

    fetch(`/projects/${selectedProject}/add_insight`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ message_id: message.unique_id }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to save insight');
        }
        return response.json();
      })
      .then(data => {
        handleClose();
        setSnackbarOpen(true); // Open the snackbar on successful save
      })
      .catch(error => {
        console.error('Error saving insight:', error);
        // Handle the error (e.g., show an error message to the user)
      });
  };

  const handleAddNewProject = () => {
    setShowNewProject(true);
  };

  const handleSaveNewProject = () => {
    if (newProjectName.trim() === '') {
      return; // Do nothing if the project name is empty
    }

    fetch('/projects', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ title: newProjectName }),
    })
      .then(response => response.json())
      .then(newProject => {
        setProjects([...projects, newProject]);
        setSelectedProject(newProject.id);
        setShowNewProject(false);
        setNewProjectName('');
      })
      .catch(error => {
        console.error('Error creating new project:', error);
      });
  };

  const handleMenuSelect = (event: SelectChangeEvent) => {
    setSelectedProject(event.target.value as string);
  }

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <span>
      <Tooltip title="Save Insight to Project">
        <IconButton onClick={(e) => handleClickOpen(e)} size="small" aria-label="Share">
          <InsightsIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
          Save Insight to Project
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <p>Please select a project to save the insight to</p>
          <Select
            value={selectedProject}
            onChange={handleMenuSelect}
            fullWidth
            displayEmpty
          >
            <MenuItem value="" disabled>Select a project</MenuItem>
            {projects.map((project) => (
              <MenuItem key={project.id} value={project.id}>{project.title}</MenuItem>
            ))}
          </Select>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddNewProject}
            size="small"
            sx={{ mt: 1, mb: 1 }}
          >
            Create new project
          </Button>
          {showNewProject && (
            <div>
              <TextField
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Enter new project name"
                fullWidth
                margin="normal"
              />
              <Button onClick={handleSaveNewProject}>Save New Project</Button>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} autoFocus>Save</Button>
        </DialogActions>
      </BootstrapDialog>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Message successfully saved as insight!
        </Alert>
      </Snackbar>
    </span>
  );
}
